import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowRight, ChevronDown, Quote, Star } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, MaskLine, FadeIn, Marquee, Overline } from "@/components/Reveal";
import api from "@/api";
import { SITE, IMAGES, FALLBACK_TREATMENTS } from "@/config/site";

const MANIFESTO = [
    {
        n: "01",
        title: "Ascolto",
        text: "Ogni trattamento inizia da una conversazione. Il corpo racconta ciò che le parole trattengono.",
    },
    {
        n: "02",
        title: "Respiro",
        text: "Rallentare è la prima cura. Nello studio il tempo si dilata e il respiro ritrova il suo ritmo naturale.",
    },
    {
        n: "03",
        title: "Presenza",
        text: "Mani attente, oli naturali, silenzio gentile. Nulla di meccanico: ogni gesto è dedicato a te.",
    },
];

const Home = () => {
    const heroRef = useRef(null);
    const [treatments, setTreatments] = useState(FALLBACK_TREATMENTS);

    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    useEffect(() => {
        api.get("/treatments")
            .then((res) => setTreatments(res.data.treatments))
            .catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            <SEO
                title="Massaggi Olistici Bologna | Sara Solferini · Studio Olistico"
                description="Studio olistico a Bologna: massaggi olistici, rilassanti e decontratturanti su appuntamento. Ritrova il tuo equilibrio."
            />

            {/* ============ HERO ============ */}
            <section ref={heroRef} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="hero-section">
                <motion.img
                    src={IMAGES.hero}
                    alt="Mani durante un trattamento olistico"
                    style={{ y: imgY, scale: imgScale }}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 via-espresso/35 to-espresso/20" />

                <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-28 pt-40 md:px-8">
                    <FadeIn delay={0.2}>
                        <span className="inline-flex items-center gap-2 rounded-full border border-cream/30 bg-cream/10 px-4 py-2 text-xs uppercase tracking-[0.25em] text-cream backdrop-blur-sm" data-testid="hero-badge">
                            <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />
                            Bologna · Su appuntamento
                        </span>
                    </FadeIn>

                    <h1 className="mt-8 font-serif text-5xl font-light leading-[1.05] tracking-tight text-cream sm:text-6xl lg:text-7xl" data-testid="hero-title">
                        <MaskLine delay={0.4}>Ritrova il tuo</MaskLine>
                        <MaskLine delay={0.55}>
                            <span className="italic">equilibrio.</span>
                        </MaskLine>
                    </h1>

                    <FadeIn delay={0.85} className="mt-7 max-w-xl">
                        <p className="text-base leading-relaxed text-cream/85 md:text-lg" data-testid="hero-subtitle">
                            Massaggi olistici e percorsi di benessere a Bologna, pensati per aiutarti a
                            rallentare, ascoltare il corpo e ritrovare una sensazione di armonia.
                        </p>
                    </FadeIn>

                    <FadeIn delay={1.05} className="mt-10 flex flex-col gap-4 sm:flex-row">
                        <Link
                            to="/prenota"
                            data-testid="hero-cta-prenota"
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-cream px-8 py-4 text-sm font-semibold uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-sage hover:text-cream"
                        >
                            Prenota il tuo trattamento
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                        </Link>
                        <Link
                            to="/trattamenti"
                            data-testid="hero-cta-trattamenti"
                            className="inline-flex items-center justify-center rounded-full border border-cream/50 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-cream/10"
                        >
                            Scopri i trattamenti
                        </Link>
                    </FadeIn>

                    <FadeIn delay={1.2}>
                        <p className="mt-5 text-xs uppercase tracking-[0.2em] text-cream/60" data-testid="hero-booking-notice">
                            Prenotazione online con preavviso di almeno 1 giorno
                        </p>
                    </FadeIn>
                </motion.div>

                <div className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 md:block">
                    <ChevronDown className="animate-drift h-6 w-6 text-cream/70" strokeWidth={1.5} />
                </div>
            </section>

            {/* ============ MARQUEE ============ */}
            <Marquee items={["Ascolto", "Respiro", "Equilibrio", "Presenza", "Calma", "Benessere"]} />

            {/* ============ INTRO / MANIFESTO ============ */}
            <section className="mx-auto max-w-6xl px-5 py-24 md:px-8 md:py-36" data-testid="intro-section">
                <div className="grid gap-16 md:grid-cols-2 md:gap-20">
                    <div>
                        <Reveal>
                            <Overline>Lo studio</Overline>
                            <h2 className="mt-5 font-serif text-3xl font-normal leading-snug tracking-tight text-ink sm:text-4xl lg:text-5xl">
                                Un rifugio intimo dove il corpo torna ad{" "}
                                <span className="italic text-sage">ascoltare</span> e la mente a{" "}
                                <span className="italic text-sage">tacere</span>.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-8 text-base leading-relaxed text-clay">
                                Lontano dalla fretta della città, lo studio accoglie poche persone al giorno,
                                solo su appuntamento. Ogni incontro è costruito con cura: luci calde,
                                tessuti naturali, profumi delicati e tutto il tempo che serve.
                            </p>
                            <Link
                                to="/chi-sono"
                                data-testid="intro-link-chi-sono"
                                className="group mt-8 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-sage"
                            >
                                Conosci chi ti accoglie
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="flex flex-col justify-center">
                        {MANIFESTO.map((item, i) => (
                            <Reveal key={item.n} delay={i * 0.12}>
                                <div className="flex gap-6 border-t border-border py-7 first:border-t-0 md:py-8" data-testid={`manifesto-${item.n}`}>
                                    <span className="font-serif text-4xl font-light text-beige">{item.n}</span>
                                    <div>
                                        <h3 className="font-serif text-2xl text-ink">{item.title}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-clay">{item.text}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ TRATTAMENTI PREVIEW ============ */}
            <section className="bg-sand py-24 md:py-32" data-testid="treatments-preview-section">
                <div className="mx-auto max-w-6xl px-5 md:px-8">
                    <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <div>
                            <Overline>I trattamenti</Overline>
                            <h2 className="mt-5 font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl lg:text-5xl">
                                Scelti con cura, <span className="italic text-sage">mai di serie</span>
                            </h2>
                        </div>
                        <Link
                            to="/trattamenti"
                            data-testid="treatments-preview-link-all"
                            className="group inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-sage"
                        >
                            Tutti i trattamenti
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                        </Link>
                    </Reveal>

                    <div className="mt-14 grid gap-6 md:grid-cols-2">
                        {treatments.slice(0, 4).map((t, i) => (
                            <Reveal key={t.id} delay={i * 0.08} className={i === 0 ? "md:row-span-2" : ""}>
                                <Link
                                    to={`/prenota?trattamento=${t.id}`}
                                    data-testid={`treatment-card-${t.id}`}
                                    className="group relative block overflow-hidden rounded-2xl shadow-[0_4px_20px_rgba(67,46,30,0.06)] transition-shadow duration-500 hover:shadow-[0_16px_48px_rgba(82,117,87,0.18)]"
                                >
                                    <div className={`overflow-hidden ${i === 0 ? "h-80 md:h-full md:min-h-[560px]" : "h-64 md:h-[268px]"}`}>
                                        <img
                                            src={t.image}
                                            alt={t.name}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-espresso/75 via-transparent to-transparent" />
                                    <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
                                        <p className="text-xs uppercase tracking-[0.25em] text-cream/70">{t.tagline}</p>
                                        <h3 className="mt-2 font-serif text-2xl text-cream md:text-3xl">{t.name}</h3>
                                        <div className="mt-3 flex items-center gap-3 text-xs text-cream/80">
                                            <span className="rounded-full bg-cream/15 px-3 py-1 backdrop-blur-sm">{t.duration}</span>
                                            <span className="rounded-full bg-cream/15 px-3 py-1 backdrop-blur-sm">{t.price}</span>
                                        </div>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ FILOSOFIA ============ */}
            <section className="mx-auto max-w-6xl px-5 py-24 md:px-8 md:py-36" data-testid="philosophy-section">
                <div className="grid items-center gap-14 md:grid-cols-5 md:gap-20">
                    <Reveal className="md:col-span-2">
                        <div className="overflow-hidden rounded-2xl rounded-t-[10rem]">
                            <img
                                src={IMAGES.oil}
                                alt="Dipinto ad acquerello con spirale Equilibrio"
                                loading="lazy"
                                className="h-auto w-full transition-transform duration-700 hover:scale-105"
                            />
                        </div>
                    </Reveal>
                    <div className="md:col-span-3">
                        <Reveal>
                            <Quote className="h-8 w-8 text-beige" strokeWidth={1.5} />
                            <h2 className="mt-6 font-serif text-3xl font-normal italic leading-snug tracking-tight text-ink sm:text-4xl">
                                “Il benessere non si rincorre. Si coltiva, un respiro alla volta.”
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-8 text-base leading-relaxed text-clay">
                                La filosofia dello studio è semplice: dedicare attenzione vera a una persona
                                alla volta. Non promettiamo miracoli, ma uno spazio protetto in cui il corpo
                                può sciogliersi e la mente può finalmente posarsi.
                            </p>
                            <Link
                                to="/lo-studio"
                                data-testid="philosophy-link-studio"
                                className="group mt-8 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-sage"
                            >
                                Entra nello studio
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============ STUDIO PREVIEW ============ */}
            <section className="bg-sage-light/50 py-24 md:py-32" data-testid="studio-preview-section">
                <div className="mx-auto max-w-6xl px-5 md:px-8">
                    <Reveal className="mx-auto max-w-2xl text-center">
                        <Overline className="justify-center">Lo spazio</Overline>
                        <h2 className="mt-5 font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl">
                            Luce naturale, legno, <span className="italic text-sage">silenzio</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
                        {[
                            { src: IMAGES.candles, alt: "Candele e tessuti naturali", cls: "col-span-2 md:col-span-1" },
                            { src: IMAGES.ambiance, alt: "Atmosfera dello studio", cls: "" },
                            { src: IMAGES.session, alt: "Dettaglio di un trattamento", cls: "" },
                        ].map((img, i) => (
                            <Reveal key={img.src} delay={i * 0.1} className={img.cls}>
                                <div className="overflow-hidden rounded-2xl">
                                    <img
                                        src={img.src}
                                        alt={img.alt}
                                        loading="lazy"
                                        className="h-56 w-full object-cover transition-transform duration-700 hover:scale-105 md:h-72"
                                    />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ RECENSIONI PREVIEW (placeholder onesto) ============ */}
            <section className="mx-auto max-w-6xl px-5 py-24 md:px-8 md:py-32" data-testid="reviews-preview-section">
                <Reveal className="mx-auto max-w-2xl text-center">
                    <Overline className="justify-center">Le voci dei clienti</Overline>
                    <h2 className="mt-5 font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl">
                        Chi è passato di qui <span className="italic text-sage">torna</span>
                    </h2>
                    <div className="mt-6 flex items-center justify-center gap-3" data-testid="reviews-rating-preview">
                        <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} className="h-4 w-4 fill-sage text-sage" strokeWidth={1} />
                            ))}
                        </div>
                        <span className="text-sm text-clay">4,8 su 5 · 26 recensioni su Google</span>
                    </div>
                    <p className="mt-6 font-serif text-xl font-light italic leading-relaxed text-clay" data-testid="reviews-placeholder-text">
                        “Sin da quando sono entrata nel tuo studio mi sono sentita accolta e coccolata…
                        sono uscita come nuova!”
                    </p>
                    <p className="mt-3 text-xs uppercase tracking-[0.25em] text-fog">Ale · cliente dello studio</p>
                    <Link
                        to="/recensioni"
                        data-testid="reviews-preview-link"
                        className="group mt-8 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-sage"
                    >
                        Leggi tutte le recensioni
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                    </Link>
                </Reveal>
            </section>

            {/* ============ FINAL CTA ============ */}
            <section className="px-5 pb-28 md:px-8" data-testid="final-cta-section">
                <Reveal>
                    <div className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl bg-sage px-6 py-20 text-center md:py-28">
                        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url(${IMAGES.candles})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                        <div className="relative">
                            <h2 className="font-serif text-4xl font-light tracking-tight text-cream sm:text-5xl" data-testid="final-cta-title">
                                Regalati un momento <span className="italic">per te</span>
                            </h2>
                            <p className="mx-auto mt-6 max-w-md text-base text-cream/85">
                                Scegli il trattamento, il giorno e l’orario. La conferma arriva subito,
                                direttamente nella tua email.
                            </p>
                            <Link
                                to="/prenota"
                                data-testid="final-cta-banner-prenota"
                                className="mt-10 inline-block rounded-full bg-cream px-10 py-4 text-sm font-semibold uppercase tracking-widest text-sage-dark transition-colors duration-300 hover:bg-espresso hover:text-cream"
                            >
                                Prenota ora
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
};

export default Home;
